import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { ItemService } from '../../services/item.service';
import { SupplierService } from '../../services/supplier.service';
import { AuthService } from '../../services/auth.service';
import {
  PurchaseOrder,
  PurchaseOrderCreateDTO,
  PurchaseOrderLine,
  PurchaseOrderStatus,
} from '../../interfaces/procurement/purchaseOrder.interface';
import { Item } from '../../interfaces/item.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { useToast } from '../../context/ToastContext';
import { SUPPLIER_READ_ROLES } from '../../constants/procurementRoles';

// GoodsReceiptController.create's @PreAuthorize: StoreAdmin, InstitutionAdmin.
const RECEIVE_ROLES = ['StoreAdmin', 'InstitutionAdmin'];
const RECEIVABLE_STATUSES: PurchaseOrderStatus[] = ['ORDERED', 'PARTIALLY_RECEIVED'];

type Action = 'edit' | 'save' | 'cancelEdit' | 'submit' | 'approve' | 'reject' | 'markOrdered' | 'cancel' | 'amend' | 'delete';

function actionsForStatus(status: PurchaseOrderStatus, editMode: boolean): Action[] {
  if (editMode) return ['save', 'cancelEdit'];
  switch (status) {
    case 'DRAFT': return ['edit', 'submit', 'cancel', 'delete'];
    case 'PENDING_APPROVAL': return ['cancel'];
    case 'APPROVED': return ['markOrdered', 'cancel'];
    case 'ORDERED':
    case 'PARTIALLY_RECEIVED': return ['amend', 'cancel'];
    case 'RECEIVED':
    case 'CLOSED': return ['amend'];
    default: return [];
  }
}

interface LineDraft {
  itemId: string;
  quantity: number;
  unitPrice: number;
}

function linesToDraft(lines: PurchaseOrderLine[]): LineDraft[] {
  return lines.map(l => ({ itemId: l.itemId, quantity: l.quantity, unitPrice: l.unitPrice }));
}

function linesChanged(original: LineDraft[], draft: LineDraft[]): boolean {
  if (original.length !== draft.length) return true;
  return original.some((o, i) =>
    o.itemId !== draft[i].itemId || o.quantity !== draft[i].quantity || o.unitPrice !== draft[i].unitPrice
  );
}

const STATUS_COLORS: Record<PurchaseOrderStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'info',
  ORDERED: 'primary',
  PARTIALLY_RECEIVED: 'secondary',
  RECEIVED: 'success',
  CLOSED: 'default',
  REJECTED: 'error',
  CANCELLED: 'error',
};

export const PurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const rawRoles = AuthService.getCurrentUser()?.roles;
  const userRoles: string[] = Array.isArray(rawRoles) ? rawRoles : rawRoles ? [rawRoles] : [];
  const canReadSuppliers = SUPPLIER_READ_ROLES.some(r => userRoles.includes(r));

  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [draftLines, setDraftLines] = useState<LineDraft[]>([]);
  const [draftExpectedDate, setDraftExpectedDate] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftSupplierId, setDraftSupplierId] = useState('');
  const [draftOrderDate, setDraftOrderDate] = useState('');
  const [draftTaxAmount, setDraftTaxAmount] = useState(0);
  const [reapprovalBannerVisible, setReapprovalBannerVisible] = useState(false);

  const loadPO = useCallback(async () => {
    if (!id) return;
    try {
      const result = await PurchaseOrderService.getById(id);
      setPo(result);
    } catch (e) {
      toast.showApiError(e, 'Failed to load purchase order');
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    Promise.all([
      loadPO(),
      ItemService.getItemsByInstitution(0, 500).then(r => setItems(r.content)),
      // SupplierController GETs 403 every other role (e.g. InstitutionManager): skip the call for
      // them, and never let a failed supplier lookup reject out of the Promise.all -- the PO must
      // still display (the supplier simply falls back to its id).
      canReadSuppliers
        ? SupplierService.listByInstitution(0, 500).then(r => setSuppliers(r.content)).catch(() => undefined)
        : Promise.resolve(),
    ]).finally(() => setLoading(false));
  }, [loadPO]); // eslint-disable-line react-hooks/exhaustive-deps

  const enterEditMode = () => {
    if (!po) return;
    setDraftLines(linesToDraft(po.lines));
    setDraftExpectedDate(po.expectedDate ?? '');
    setDraftNotes(po.notes ?? '');
    setDraftSupplierId(po.supplierId);
    setDraftOrderDate(po.orderDate);
    setDraftTaxAmount(po.taxAmount);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
  };

  const fullDto = (): PurchaseOrderCreateDTO => ({
    supplierId: draftSupplierId,
    orderDate: draftOrderDate,
    expectedDate: draftExpectedDate || undefined,
    notes: draftNotes || undefined,
    taxAmount: draftTaxAmount,
    institutionId: AuthService.getSelectedInstitution()!!,
    lines: draftLines.map(l => ({ itemId: l.itemId, quantity: l.quantity, unitPrice: l.unitPrice })),
  });

  const handleSave = async () => {
    if (!po) return;
    try {
      setSaving(true);
      if (po.status === 'DRAFT') {
        await PurchaseOrderService.updateDraft(po.id, fullDto());
      } else {
        const originalLines = linesToDraft(po.lines);
        const onlyHeaderChanged =
          !linesChanged(originalLines, draftLines) &&
          draftSupplierId === po.supplierId &&
          draftOrderDate === po.orderDate &&
          draftTaxAmount === po.taxAmount;

        if (onlyHeaderChanged) {
          await PurchaseOrderService.applyMinorAmendment(po.id, {
            expectedDate: draftExpectedDate || undefined,
            notes: draftNotes || undefined,
          });
        } else {
          const updated = await PurchaseOrderService.applyMaterialAmendment(po.id, fullDto());
          if (updated.status === 'PENDING_APPROVAL') setReapprovalBannerVisible(true);
        }
      }
      await loadPO();
      setEditMode(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!po) return;
    try {
      await PurchaseOrderService.submit(po.id);
      toast.showSuccess('Purchase order submitted for approval');
      await loadPO();
    } catch (e) {
      toast.showApiError(e, 'Failed to submit purchase order');
    }
  };

  const handleMarkOrdered = async () => {
    if (!po) return;
    try {
      await PurchaseOrderService.markOrdered(po.id);
      toast.showSuccess('Purchase order marked as ordered');
      await loadPO();
    } catch (e) {
      toast.showApiError(e, 'Failed to mark as ordered');
    }
  };

  const handleCancel = async () => {
    if (!po) return;
    try {
      await PurchaseOrderService.cancel(po.id);
      toast.showSuccess('Purchase order cancelled');
      await loadPO();
    } catch (e) {
      toast.showApiError(e, 'Failed to cancel purchase order');
    }
  };

  const handleDelete = async () => {
    if (!po || !window.confirm('Delete this purchase order? This cannot be undone.')) return;
    try {
      await PurchaseOrderService.delete(po.id);
      toast.showSuccess('Purchase order deleted');
      navigate('/procurement/purchase-orders');
    } catch (e) {
      toast.showApiError(e, 'Failed to delete purchase order');
    }
  };

  const handleDownloadPdf = async () => {
    if (!po) return;
    setDownloadingPdf(true);
    try {
      await PurchaseOrderService.downloadPdf(po.id);
    } catch (e) {
      toast.showApiError(e, 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const updateDraftLine = (idx: number, patch: Partial<LineDraft>) => {
    setDraftLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!po) return <Typography sx={{ p: 3 }}>Purchase order not found</Typography>;

  const actions = actionsForStatus(po.status, editMode);
  const canReceive = !editMode
    && RECEIVABLE_STATUSES.includes(po.status)
    && userRoles.some(r => RECEIVE_ROLES.includes(r));

  return (
    <Box sx={{ p: 3 }}>
      {reapprovalBannerVisible && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Material amendment detected — PO sent back for re-approval.
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">{po.poNumber}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip label={po.status} color={STATUS_COLORS[po.status]} size="small" />
            <Typography variant="body2" color="text.secondary">Rev {po.revision}</Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={handleDownloadPdf} disabled={downloadingPdf}>
            {downloadingPdf ? 'Preparing…' : 'Download as PDF'}
          </Button>
          {canReceive && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/procurement/goods-receipts/new?purchaseOrderId=${po.id}`)}
            >
              Receive goods
            </Button>
          )}
          {actions.includes('edit') && (
            <Button variant="outlined" onClick={enterEditMode}>Edit</Button>
          )}
          {actions.includes('amend') && (
            <Button variant="outlined" onClick={enterEditMode}>Amend</Button>
          )}
          {actions.includes('submit') && (
            <Button variant="contained" color="primary" onClick={handleSubmit}>Submit</Button>
          )}
          {actions.includes('markOrdered') && (
            <Button variant="contained" color="primary" onClick={handleMarkOrdered}>Mark Ordered</Button>
          )}
          {actions.includes('cancel') && (
            <Button variant="outlined" color="warning" onClick={handleCancel}>Cancel PO</Button>
          )}
          {actions.includes('delete') && (
            <Button variant="outlined" color="error" onClick={handleDelete}>Delete</Button>
          )}
          {actions.includes('save') && (
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>Save</Button>
          )}
          {actions.includes('cancelEdit') && (
            <Button variant="outlined" onClick={cancelEdit}>Discard</Button>
          )}
        </Stack>
      </Box>

      {/* Summary */}
      <Paper sx={{ p: 2, mb: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {editMode ? (
          <>
            <TextField
              select
              label="Supplier"
              value={draftSupplierId}
              onChange={e => setDraftSupplierId(e.target.value)}
            >
              {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            <TextField
              label="Order Date"
              type="date"
              value={draftOrderDate}
              onChange={e => setDraftOrderDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Expected Date"
              type="date"
              value={draftExpectedDate}
              onChange={e => setDraftExpectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Tax Amount"
              type="number"
              value={draftTaxAmount}
              onChange={e => setDraftTaxAmount(Number(e.target.value))}
            />
            <TextField
              sx={{ gridColumn: '1 / -1' }}
              label="Notes"
              multiline
              rows={2}
              value={draftNotes}
              onChange={e => setDraftNotes(e.target.value)}
            />
          </>
        ) : (
          <>
            <Typography variant="body2"><strong>Supplier:</strong> {po.supplierId}</Typography>
            <Typography variant="body2"><strong>Order Date:</strong> {po.orderDate}</Typography>
            <Typography variant="body2">
              <strong>Expected Date:</strong> {po.expectedDate ?? '-'}
            </Typography>
            <Typography variant="body2"><strong>Tax Amount:</strong> {po.taxAmount}</Typography>
            <Typography variant="body2" sx={{ gridColumn: '1 / -1' }}>
              <strong>Notes:</strong> {po.notes ?? '-'}
            </Typography>
          </>
        )}
      </Paper>

      {/* Lines */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lines</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Unit Price</TableCell>
              <TableCell>Line Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editMode ? (
              draftLines.map((l, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Item"
                      value={l.itemId}
                      onChange={e => updateDraftLine(idx, { itemId: e.target.value })}
                    >
                      {items.map(i => <MenuItem key={i.id} value={i.id}>{i.itemName}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      label="Quantity"
                      value={l.quantity}
                      onChange={e => updateDraftLine(idx, { quantity: Number(e.target.value) })}
                      inputProps={{ 'aria-label': 'Quantity' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      label="Unit Price"
                      value={l.unitPrice}
                      onChange={e => updateDraftLine(idx, { unitPrice: Number(e.target.value) })}
                    />
                  </TableCell>
                  <TableCell>{(l.quantity * l.unitPrice).toFixed(4)}</TableCell>
                </TableRow>
              ))
            ) : (
              po.lines.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{items.find(i => i.id === l.itemId)?.itemName ?? l.itemId}</TableCell>
                  <TableCell>{l.quantity}</TableCell>
                  <TableCell>{l.unitPrice}</TableCell>
                  <TableCell>{l.lineTotal}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Totals */}
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        <Typography>Subtotal: {po.subtotal}</Typography>
        <Typography>Tax: {po.taxAmount}</Typography>
        <Typography variant="h6">Total: {po.totalAmount}</Typography>
      </Paper>
    </Box>
  );
};

export default PurchaseOrderDetail;
